import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, List, Tag, Button, Space, Segmented, message } from 'antd';
import { FacebookFilled, BellOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

// type can be: campaign | billing | integration | system

const typeColor = (t) => ({ campaign: 'blue', billing: 'orange', integration: 'purple' }[t] || 'default');

// Multicolor Google 'G' logo (SVG), sized via style prop
const GoogleGIcon = ({ style }) => (
  <svg viewBox="0 0 48 48" style={{ width: 18, height: 18, display: 'inline-block', verticalAlign: 'middle', ...(style || {}) }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.72 1.22 9.23 3.6l6.89-6.89C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.6 13.58 17.86 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.63-.15-3.19-.44-4.69H24v9.09h12.7c-.55 2.97-2.19 5.49-4.66 7.19l7.12 5.53C43.78 37.04 46.5 31.35 46.5 24.5z"/>
    <path fill="#FBBC05" d="M10.54 19.41l-7.98-6.19C.89 16.6 0 20.2 0 24c0 3.72.89 7.27 2.47 10.42l8.07-6.24C9.84 26.54 9.5 25.31 9.5 24c0-1.12.2-2.2.54-3.09z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.95-2.14 15.93-5.89l-7.12-5.53C31.08 38.71 27.8 40 24 40c-6.14 0-11.4-4.08-13.46-9.91l-8.07 6.24C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const Notifications = () => {
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [systemLoading, setSystemLoading] = useState(false);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  const fetchList = async (status) => {
    try {
      setLoading(true);
      
      // Check if token exists
      if (!token) {
        message.error('Authentication required. Please log in.');
        logout();
        navigate('/login');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      const qs = new URLSearchParams({ status });
      const res = await fetch(`/api/notifications?${qs.toString()}`, { headers });
      
      // Handle 401 Unauthorized
      if (res.status === 401) {
        message.error('Session expired. Please log in again.');
        logout();
        navigate('/login');
        return;
      }
      
      if (!res.ok) throw new Error('Failed to load notifications');
      const json = await res.json();
      const rows = Array.isArray(json?.items) ? json.items : [];
      setItems(rows.map(r => ({
        id: r.id,
        title: r.title,
        desc: r.description,
        type: r.type || 'system',
        read: !!r.read,
        time: r.created_at || '',
        metadata: r.metadata || null,
      })));
    } catch (e) {
      message.error(e?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const toggleReadBulk = async (ids) => {
    try {
      if (!token) {
        message.error('Authentication required. Please log in.');
        logout();
        navigate('/login');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      const responses = await Promise.all(ids.map(id => fetch(`/api/notifications/${id}/toggle`, { method: 'POST', headers })));
      
      // Check for 401 in any response
      if (responses.some(res => res.status === 401)) {
        message.error('Session expired. Please log in again.');
        logout();
        navigate('/login');
        return;
      }
      
      // Optimistic update
      setItems(prev => prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n));
      message.success('Selected notifications marked as read');
    } catch (e) {
      message.error(e?.message || 'Failed to mark group as read');
    }
  };

  const markGroupRead = (groupItems) => {
    const unreadIds = groupItems.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length === 0) return message.info('No unread notifications in this group');
    toggleReadBulk(unreadIds);
  };

  const fetchSystemNotifications = async () => {
    try {
      setSystemLoading(true);

      if (!token) {
        message.error('Authentication required. Please log in.');
        logout();
        navigate('/login');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch('/api/system-notifications', { headers });

      if (res.status === 401) {
        message.error('Session expired. Please log in again.');
        logout();
        navigate('/login');
        return;
      }

      if (!res.ok) throw new Error('Failed to load system notifications');
      const json = await res.json();
      const rows = Array.isArray(json?.items) ? json.items : [];
      setSystemNotifications(rows.map(r => ({
        id: `system-${r.id}`,
        title: r.title,
        desc: r.message,
        type: r.type || 'info',
        priority: r.priority || 'normal',
        isRead: r.is_read || false,
        time: r.created_at || '',
        systemNotificationId: r.id,
      })));
    } catch (e) {
      message.error(e?.message || 'Failed to load system notifications');
    } finally {
      setSystemLoading(false);
    }
  };

  useEffect(() => {
    fetchList(filter);
    fetchSystemNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, token]);

  const filtered = items; // server filters by status

  // Partition and sort (newest first)
  const googleItems = filtered
    .filter(n => (n.metadata?.platform === 'google_ads'))
    .slice()
    .sort((a, b) => new Date(b.time) - new Date(a.time));
  const facebookItems = filtered
    .filter(n => (n.metadata?.platform === 'facebook'))
    .slice()
    .sort((a, b) => new Date(b.time) - new Date(a.time));
  const googleUnread = googleItems.filter(n => !n.read).length;
  const facebookUnread = facebookItems.filter(n => !n.read).length;

  const markAllRead = async () => {
    try {
      if (!token) {
        message.error('Authentication required. Please log in.');
        logout();
        navigate('/login');
        return;
      }
      
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
      const res = await fetch('/api/notifications/mark-all-read', { method: 'POST', headers });
      
      if (res.status === 401) {
        message.error('Session expired. Please log in again.');
        logout();
        navigate('/login');
        return;
      }
      
      if (!res.ok) throw new Error('Failed to mark all read');
      message.success('All notifications marked as read');
      fetchList(filter);
    } catch (e) {
      message.error(e?.message || 'Failed to mark all read');
    }
  };

  const toggleRead = async (id) => {
    try {
      if (!token) {
        message.error('Authentication required. Please log in.');
        logout();
        navigate('/login');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`/api/notifications/${id}/toggle`, { method: 'POST', headers });
      
      if (res.status === 401) {
        message.error('Session expired. Please log in again.');
        logout();
        navigate('/login');
        return;
      }
      
      if (!res.ok) throw new Error('Failed to toggle');
      // Optimistic update
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: !n.read } : n));
    } catch (e) {
      message.error(e?.message || 'Failed to toggle');
    }
  };

  const toggleSystemRead = async (systemNotificationId) => {
    try {
      if (!token) {
        message.error('Authentication required. Please log in.');
        logout();
        navigate('/login');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`/api/system-notifications/${systemNotificationId}/read`, {
        method: 'POST',
        headers
      });

      if (res.status === 401) {
        message.error('Session expired. Please log in again.');
        logout();
        navigate('/login');
        return;
      }

      if (!res.ok) throw new Error('Failed to mark as read');

      // Update local state
      setSystemNotifications(prev =>
        prev.map(n => n.systemNotificationId === systemNotificationId ? { ...n, isRead: true } : n)
      );
    } catch (e) {
      message.error(e?.message || 'Failed to mark notification as read');
    }
  };

  const markAllSystemRead = async () => {
    try {
      if (!token) {
        message.error('Authentication required. Please log in.');
        logout();
        navigate('/login');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch('/api/system-notifications/mark-all-read', {
        method: 'POST',
        headers
      });

      if (res.status === 401) {
        message.error('Session expired. Please log in again.');
        logout();
        navigate('/login');
        return;
      }

      if (!res.ok) throw new Error('Failed to mark all as read');

      // Update local state
      setSystemNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true }))
      );
      message.success('All system notifications marked as read');
    } catch (e) {
      message.error(e?.message || 'Failed to mark all system notifications as read');
    }
  };

  const clearAll = async () => {
    try {
      if (!token) {
        message.error('Authentication required. Please log in.');
        logout();
        navigate('/login');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch('/api/notifications/clear-all', { method: 'DELETE', headers });
      
      if (res.status === 401) {
        message.error('Session expired. Please log in again.');
        logout();
        navigate('/login');
        return;
      }
      
      if (!res.ok) throw new Error('Failed to clear');
      setItems([]);
      message.success('All notifications cleared');
    } catch (e) {
      message.error(e?.message || 'Failed to clear notifications');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Notifications' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Notifications</Title>
        <Space>
          <Segmented
            options={[{ label: 'All', value: 'all' }, { label: 'Unread', value: 'unread' }, { label: 'Read', value: 'read' }]}
            value={filter}
            onChange={setFilter}
          />
          <Button onClick={markAllRead}>Mark all read</Button>
          <Button danger onClick={clearAll}>Clear all</Button>
        </Space>
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card
          title={<Space>
            <GoogleGIcon />
            <span>Google{googleUnread ? ` (${googleUnread})` : ''}</span>
          </Space>}
          extra={<Space>
            <Button onClick={() => markGroupRead(googleItems)} disabled={googleUnread === 0}>Mark group read</Button>
            <Button loading={loading} onClick={() => fetchList(filter)}>Refresh</Button>
          </Space>}
        >
          <List
            dataSource={googleItems}
            locale={{ emptyText: 'No Google notifications' }}
            loading={loading}
            renderItem={(n) => (
              <List.Item
                actions={[
                  <Button key="toggle" size="small" onClick={() => toggleRead(n.id)}>{n.read ? 'Mark unread' : 'Mark read'}</Button>,
                ]}
              >
                <List.Item.Meta
                  title={<Space>
                    <Tag color={typeColor(n.type)}>{n.type}</Tag>
                    <Tag color="#4285F4">Google</Tag>
                    <span style={{ fontWeight: n.read ? 400 : 600 }}>{n.title}</span>
                  </Space>}
                  description={<>
                    <div style={{ color: '#666' }}>{n.desc}</div>
                    <div style={{ color: '#999', marginTop: 4 }}>{n.time}</div>
                  </>}
                />
              </List.Item>
            )}
          />
        </Card>

        <Card
          title={<Space><FacebookFilled style={{ color: '#1877f2' }} /> Facebook{facebookUnread ? ` (${facebookUnread})` : ''}</Space>}
          extra={<Space>
            <Button onClick={() => markGroupRead(facebookItems)} disabled={facebookUnread === 0}>Mark group read</Button>
            <Button loading={loading} onClick={() => fetchList(filter)}>Refresh</Button>
          </Space>}
        >
          <List
            dataSource={facebookItems}
            locale={{ emptyText: 'No Facebook notifications' }}
            loading={loading}
            renderItem={(n) => (
              <List.Item
                actions={[
                  <Button key="toggle" size="small" onClick={() => toggleRead(n.id)}>{n.read ? 'Mark unread' : 'Mark read'}</Button>,
                ]}
              >
                <List.Item.Meta
                  title={<Space>
                    <Tag color={typeColor(n.type)}>{n.type}</Tag>
                    <Tag>Facebook</Tag>
                    <span style={{ fontWeight: n.read ? 400 : 600 }}>{n.title}</span>
                  </Space>}
                  description={<>
                    <div style={{ color: '#666' }}>{n.desc}</div>
                    <div style={{ color: '#999', marginTop: 4 }}>{n.time}</div>
                  </>}
                />
              </List.Item>
            )}
          />
        </Card>

        <Card
          title={<Space><BellOutlined /> System{(() => {
            const systemUnread = systemNotifications.filter(n => !n.isRead).length;
            return systemUnread ? ` (${systemUnread})` : '';
          })()}</Space>}
          extra={<Space>
            <Button onClick={() => {
              const unreadSystem = systemNotifications.filter(n => !n.isRead);
              if (unreadSystem.length === 0) return message.info('No unread system notifications');
              markAllSystemRead();
            }} disabled={systemNotifications.filter(n => !n.isRead).length === 0}>Mark all read</Button>
            <Button loading={systemLoading} onClick={() => fetchSystemNotifications()}>Refresh</Button>
          </Space>}
        >
          <List
            dataSource={systemNotifications}
            locale={{ emptyText: 'No system notifications' }}
            loading={systemLoading}
            renderItem={(n) => (
              <List.Item
                actions={[
                  <Button key="toggle" size="small" onClick={() => toggleSystemRead(n.systemNotificationId)}>{n.isRead ? 'Mark unread' : 'Mark read'}</Button>,
                ]}
              >
                <List.Item.Meta
                  title={<Space>
                    <Tag color={typeColor(n.type)}>{n.type}</Tag>
                    <Tag color={
                      n.priority === 'urgent' ? 'red' :
                      n.priority === 'high' ? 'orange' :
                      n.priority === 'normal' ? 'blue' : 'green'
                    }>{n.priority}</Tag>
                    <span style={{ fontWeight: n.isRead ? 400 : 600 }}>{n.title}</span>
                  </Space>}
                  description={<>
                    <div style={{ color: '#666' }}>{n.desc}</div>
                    <div style={{ color: '#999', marginTop: 4 }}>{n.time ? new Date(n.time).toLocaleString() : ''}</div>
                  </>}
                />
              </List.Item>
            )}
          />
        </Card>
      </Space>
    </div>
  );
};

export default Notifications;
